import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { t, getLocalizedPath, language } = useLanguage();

  const solutions =
    language === "fr"
      ? [
          { label: "Application de reçus", href: "/application-recus" },
          { label: "Retours en magasin", href: "/application-retours-magasin" },
          { label: "Garanties et preuves d'achat", href: "/application-garanties" },
          { label: "Reçus pour la famille", href: "/application-recus-famille" },
        ]
      : [
          { label: "Receipt app", href: "/en/receipt-app" },
          { label: "In-store returns", href: "/en/returns-app" },
          { label: "Warranties & proof of purchase", href: "/en/warranty-tracker-app" },
          { label: "Family receipts", href: "/en/family-receipt-app" },
        ];

  // Merchant receipt pages — kept in the footer so they are reachable from
  // anywhere on the site (and by crawlers) without extra navigation.
  const merchants =
    language === "fr"
      ? [
          { label: "Reçus Canadian Tire", href: "/marchands/recus-canadian-tire" },
          { label: "Reçus IKEA", href: "/marchands/recus-ikea" },
          { label: "Reçus Home Depot", href: "/marchands/recus-home-depot" },
          { label: "Reçus RONA", href: "/marchands/recus-rona" },
        ]
      : [
          { label: "Canadian Tire receipts", href: "/en/merchants/canadian-tire-receipts" },
          { label: "IKEA receipts", href: "/en/merchants/ikea-receipts" },
          { label: "Home Depot receipts", href: "/en/merchants/home-depot-receipts" },
          { label: "RONA receipts", href: "/en/merchants/rona-receipts" },
        ];

  return (
    <footer className="bg-secondary/50 relative">
      {/* Gradient top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Newsletter callout / separator band */}
      <div className="border-b border-border/50">
        <div className="container mx-auto px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-heading text-lg text-foreground tracking-tight">
              {t("footer.stayInLoop")}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {t("footer.followAlong")}
            </p>
          </div>
          <a
            href="mailto:info@refoundapp.ca"
            className="inline-flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/20 px-5 py-2.5 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            <Mail className="h-4 w-4" />
            {t("footer.getInTouch")}
          </a>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to={getLocalizedPath("/")} className="inline-flex items-center gap-3 mb-5 group">
              <img
                src="/logo-test-refound-15.jpeg"
                alt="ReFound"
                className="h-9 w-9 rounded-lg transition-transform duration-300 group-hover:scale-105"
              />
              <img
                src="/logo-test-myreceipt-17.png"
                alt="ReFound"
                className="h-6"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-[220px]">
              Smarter Receipts. Easy Returns.
            </p>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-5">
              {language === "fr" ? "Solutions" : "Solutions"}
            </h3>
            <ul className="space-y-3">
              {solutions.map((s) => (
                <li key={s.href}>
                  <Link
                    to={s.href}
                    className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={getLocalizedPath("/blog")}
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Merchants */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-5">
              {language === "fr" ? "Marchands" : "Merchants"}
            </h3>
            <ul className="space-y-3">
              {merchants.map((m) => (
                <li key={m.href}>
                  <Link
                    to={m.href}
                    className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                  >
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-5">
              {t("footer.legal")}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to={getLocalizedPath("/privacy")}
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                >
                  {t("footer.privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedPath("/terms")}
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                >
                  {t("footer.termsOfService")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-5">
              {t("header.support")}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to={getLocalizedPath("/support")}
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                >
                  {t("footer.contactUs")}
                </Link>
              </li>
              <li>
                <a
                  href="mailto:info@refoundapp.ca"
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                >
                  info@refoundapp.ca
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar separator */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Bottom bar */}
      <div className="container mx-auto px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            &copy; {currentYear} ReFound. {t("footer.allRightsReserved")}
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            {t("footer.madeInCanada")}
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-3.5 w-3.5 text-destructive/80"
              aria-hidden="true"
            >
              <path d="M12 2C9.5 5.5 5 4 5 8c0 2 1 3 2 4l-1 1 2 1-1 2h2l1 4h4l1-4h2l-1-2 2-1-1-1c1-1 2-2 2-4 0-4-4.5-3.5-7-6z" />
            </svg>
          </p>
        </div>
      </div>
    </footer>
  );
}
